import { Button } from "@/components/ui/button";
import { toast } from "@/components/ui/use-toast";
import { SelecaoCompraGiro } from "@/components/CompraRapidaGiro";
import { DadosResumoGiro } from "@/dtos/Request/FinalizarCompraSorteioGiroDto";
import { FormasPagamento } from "@/dtos/FormasPagamento";
import AppService from "@/services/AppService";
import CompraService from "@/services/CompraService";
import { PresenteadoDto } from "@/services/AcessoService";
import { useAuth } from "@/contexts/AuthProvider";
import StorageUtils from "@/utils/StorageUtils";
import { montarPayloadSorteioGiro } from "@/utils/montarPayloadSorteioGiro";
import { clearSelecaoGiroCheckout } from "@/utils/selecaoGiroCheckout";
import { DrawerPagamentoParaOutraPessoa } from "@/pages/FinalizarCompra/DrawerPagamentoParaOutraPessoa";
import { FinalizarCompraTermos } from "@/pages/FinalizarCompra/FinalizarCompraTermos";
import { labelSingularLower } from "@/utils/copyTituloBilhete";
import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { FinalizarCompraGiroResumo } from "./FinalizarCompraGiroResumo";

interface FinalizarCompraGiroSaldoProps {
    saldoDisponivel: number;
    dadosResumo: DadosResumoGiro;
    selecao: SelecaoCompraGiro;
    token?: string;
    presenteados: PresenteadoDto[];
    handlePresenteados: (action: "remove" | "load", presenteado?: PresenteadoDto) => void;
}

export function FinalizarCompraGiroSaldo({
    saldoDisponivel,
    dadosResumo,
    selecao,
    token,
    presenteados,
    handlePresenteados,
}: FinalizarCompraGiroSaldoProps) {
    const [termosAceito, setTermosAceito] = useState(false);
    const [realizandoPagamento, setRealizandoPagamento] = useState(false);
    const navigate = useNavigate();
    const { atualizarSaldoDisponivel } = useAuth();
    const [searchParams] = useSearchParams();

    async function handleFinalizarCompra() {
        try {
            setRealizandoPagamento(true);

            let idPresenteado: number | undefined;
            const getIdPessoaPresenteada = searchParams.get("idPessoaPresenteada");
            if (getIdPessoaPresenteada) {
                idPresenteado = Number(getIdPessoaPresenteada);
            }

            const payload = montarPayloadSorteioGiro(selecao, {
                Origem: token ? "IOS" : "Web",
                FormaPagamento: FormasPagamento.Saldo,
                IdentificadorDispositivo: await StorageUtils.getDispositivo(),
                IdPresenteado: idPresenteado,
                IdVendedor: StorageUtils.getIdVendedor(),
            });

            if (token) {
                await AppService.FinalizarCompraSorteioGiroSaldoApp(payload, token);
                toast({
                    variant: "success",
                    description: `Feche o navegador e acesse o histórico de compras em seu aplicativo para visualizar seu ${labelSingularLower()}. Em alguns instantes você receberá seu ${labelSingularLower()} por e-mail. Boa sorte!`,
                    title: "Compra com saldo de crédito",
                    duration: 6000,
                });
            } else {
                await CompraService.FinalizarCompraSorteioGiroSaldo(payload);
                toast({
                    variant: "success",
                    description: `Sua compra foi confirmada e você já está concorrendo. Em alguns instantes você receberá seu ${labelSingularLower()} por e-mail. Boa sorte!`,
                    title: "Compra com saldo de crédito",
                    duration: 6000,
                });
                atualizarSaldoDisponivel();
                navigate("/compra/titulos?tab=giro");
            }

            clearSelecaoGiroCheckout();
            StorageUtils.removeIdVendedor();
        } catch (error: unknown) {
            setRealizandoPagamento(false);
            toast({
                variant: "destructive",
                description: String(error),
                duration: 4000,
            });
        }
    }

    return (
        <div className="md:w-5/12">
            <div className="flex flex-col md:flex-row">
                <FinalizarCompraGiroResumo dadosResumo={dadosResumo} />
            </div>
            {saldoDisponivel >= dadosResumo.valorTotal && (
                <div className="flex flex-col gap-5">
                    <DrawerPagamentoParaOutraPessoa
                        handlePresenteados={handlePresenteados}
                        presenteados={presenteados}
                    />
                    <FinalizarCompraTermos
                        aceitarTermos={async (termos) => setTermosAceito(termos)}
                        termosAceito={termosAceito}
                    />
                    <Button
                        className="w-full"
                        size="xl"
                        disabled={!termosAceito || realizandoPagamento}
                        onClick={handleFinalizarCompra}
                        variant="default"
                    >
                        {realizandoPagamento ? "Finalizando Compra..." : "Finalizar Compra"}
                    </Button>
                </div>
            )}
        </div>
    );
}
